import type { ReactNode } from 'react';

/** 44px header of every screen (design 1a/1b/1c): brand or back-title on the left, 28px glyph
 *  buttons on the right. */
export function Header({ left, right }: { left: ReactNode; right?: ReactNode }) {
  // Excel Desktop paints its own add-in "i" button over the top-right ~44px of the pane (outside our
  // DOM): keep the header actions clear of it with a wider right padding.
  return (
    <div className="flex h-11 shrink-0 items-center justify-between border-b border-line-soft pl-3.5 pr-12">
      <div className="flex min-w-0 items-center gap-2">{left}</div>
      <div className="flex items-center gap-1">{right}</div>
    </div>
  );
}

/** The Sculpt Agent mark (assets/sculpt-agent-icon.svg without its canvas): the rounded green tile with the 3×3 grid
 *  and the four-point star; the centre cell is filled white edge to edge (2026-09-28, same as sculpt-website).
 *  Inline so it scales crisply at 18 px and needs no request. */
export function SculptMark({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="100 150 780 780" className={className} role="img" aria-label="Sculpt Agent" data-testid="sculpt-mark">
      <g transform="translate(119.47,221.87)">
        <rect x="0" y="0" width="682.67" height="682.67" rx="136.53" fill="#107C41" />
        <rect x="119.47" y="119.47" width="443.73" height="443.73" rx="51.2" fill="none" stroke="#FFFFFF" strokeWidth="25.6" />
        <g stroke="#FFFFFF" strokeWidth="25.6">
          <line x1="119.47" y1="264.53" x2="563.2" y2="264.53" />
          <line x1="119.47" y1="418.13" x2="563.2" y2="418.13" />
          <line x1="264.53" y1="119.47" x2="264.53" y2="563.2" />
          <line x1="418.13" y1="119.47" x2="418.13" y2="563.2" />
        </g>
        <rect x="264.53" y="264.53" width="153.6" height="153.6" fill="#FFFFFF" />
        <circle cx="631.47" cy="51.2" r="128" fill="#FFFFFF" />
        <path d="M631.47 -51.2 L657.07 25.6 L733.87 51.2 L657.07 76.8 L631.47 153.6 L605.87 76.8 L529.07 51.2 L605.87 25.6 Z" fill="#107C41" />
      </g>
    </svg>
  );
}

/** The Sculpt Agent lockup of the sign-in screen: the mark and the name, drawn inline so the name follows the
 *  product and needs no image (the delivered lockup assets/sculpt-ai-logo.svg still reads "Sculpt AI"). */
export function SculptLockup() {
  return (
    <span className="inline-flex items-center gap-3" role="img" aria-label="Sculpt Agent" data-testid="sculpt-lockup">
      <SculptMark size={56} />
      <span aria-hidden="true" className="text-[30px] font-semibold leading-none tracking-[-0.03em]">
        <span className="text-ink">Sculpt</span> <span className="text-accent">Agent</span>
      </span>
    </span>
  );
}

/** Brand of the header: the Sculpt Agent mark alone, 26 px (product owner, 2026-09-10 — Excel already prints the
 *  add-in name above the pane, so no text here). `title` is the accessible name. */
export function Brand({ title }: { title: string; byline?: string }) {
  return (
    <span title={title} className="inline-flex shrink-0" data-testid="brand-logo">
      <SculptMark size={26} />
    </span>
  );
}

/** 28px glyph button (≡ + ← …). */
export function GlyphButton({ glyph, title, onClick, size = 13, active }: { glyph: ReactNode; title: string; onClick: () => void; size?: number; active?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`grid h-7 w-7 place-items-center rounded-[5px] leading-none transition-colors hover:bg-hover-soft ${active ? 'bg-hover-soft text-ink' : 'text-muted'}`}
      style={{ fontSize: size }}
    >
      {glyph}
    </button>
  );
}

/** History icon: a clock with a counter-clockwise arrow (16px, stroke inherits the text colour). */
export function HistoryIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** Sign-out icon: door with an arrow (16px, stroke inherits the text colour). */
export function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}
